import { useQuery } from '@tanstack/react-query';

import {
  mergeStoredSession,
  readStoredSession,
} from './companyQuestionStorage';
import {
  fetchGetCompanyQuestionSessionHistory,
  TCompanyQuestionSessionHistoryResponse,
} from './fetchGetCompanyQuestionSessionHistory';
import {
  postCompanyQuestion,
  TCompanyQuestionRequest,
} from './fetchPostCompanyQuestion';
import { useGetApplicationQuery } from '../lendingApi/lendingApiQueries';

const EMPTY_SESSION: TCompanyQuestionSessionHistoryResponse = {
  history: [],
  session_exists: false,
  completed: false,
};

export function useGetCompanyQuestion({
  contextData,
  enabled,
}: {
  contextData: TCompanyQuestionRequest['context'];
  enabled?: boolean;
}) {
  const { application } = useGetApplicationQuery();

  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: [
      'fetchGetCompanyQuestion',
      application?.application_id,
      contextData,
    ],
    queryFn: () =>
      postCompanyQuestion({
        applicationId: application?.application_id as string,
        data: {
          context: contextData,
          history: [],
        },
      }),
    enabled: enabled && Boolean(application?.application_id),
  });

  return {
    initialQuestion: data?.inference_question,
    isInitialCompanyQuestionLoading: isLoading,
    isInitialCompanyQuestionError: isError,
    isInitialCompanyQuestionSuccess: isSuccess,
  };
}

export function useGetCompanyQuestionSessionHistory() {
  const { application } = useGetApplicationQuery();
  const applicationId = application?.application_id;

  const { data, isLoading } = useQuery<TCompanyQuestionSessionHistoryResponse>({
    queryKey: ['fetchCompanyQuestionSessionHistory', applicationId],
    queryFn: async () => {
      const id = applicationId as string;
      const stored = readStoredSession(id);
      try {
        const server = await fetchGetCompanyQuestionSessionHistory(id);
        return mergeStoredSession(server, stored);
      } catch (error) {
        // Server unavailable — fall back to the per-tab mirror if we have one.
        if (stored) return stored;
        throw error;
      }
    },
    enabled: Boolean(applicationId),
    // staleTime: Infinity + refetch disabled globally (see IwQueryClient) — the
    // cache is only ever written by useCompanyQuestionMutation, so there is no
    // background refetch to race the optimistic update (no cancelQueries needed).
    staleTime: Infinity,
    retry: false,
  });

  const session = data ?? EMPTY_SESSION;

  return {
    sessionHistory: session.history,
    isSessionCompleted: session.completed,
    isSessionHistoryLoading: isLoading,
  };
}
